import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Production defaults for every editable site-settings field.
 *
 * These render on the public site whenever the admin has not (yet) filled a
 * value in the Website Settings panel. All copy is production-ready and
 * business-appropriate — no placeholders, no "coming soon" strings, no
 * fake statistics. When the owner sets a value in the admin, it overrides
 * the default here across every page that reads it.
 */
export const SITE_SETTINGS_DEFAULTS = {
  // Branding
  business_name: "Gil Maakaroun",
  logo_url: "",
  favicon_url: "",
  og_image_url: "",
  hero_image_url: "",

  // Contact
  business_address: "United States",
  business_hours: "By appointment · trading events posted on the Events page",
  contact_email: "",
  contact_phone: "",
  whatsapp_url: "",
  instagram_url: "",
  tiktok_url: "",
  facebook_url: "",
  youtube_url: "",
  google_maps_url: "",

  // Commerce
  shopify_store_url: "",

  // Homepage & site copy
  hero_headline: "The home for Panini collectors.",
  hero_subheadline:
    "Albums, packs, and Gold Crumples shipped nationwide. Missing-sticker sourcing. Local trading events. And a numbered Certificate of Completion — independently inspected and recorded by Gil Maakaroun.",
  newsletter_text:
    "A short email a few times a year — new products, upcoming trading events, and certificate program updates. Zero spam.",

  // Footer & legal
  footer_copyright: "CollectorUp. Created by Gil Maakaroun. Made for collectors.",
  announcement_bar: "",
  legal_jurisdiction: "the Commonwealth of Massachusetts, United States",
  disclaimer_footer:
    "CollectorUp's album-completion program is independently operated by Gil Maakaroun and is not affiliated with, endorsed by, or approved by Panini or FIFA.",
  disclaimer_certificate: "Independently inspected and recorded by Gil Maakaroun.",
};

/** Merge server settings over defaults; treat empty strings as "not set" so defaults win. */
function mergeSettings(server) {
  if (!server) return { ...SITE_SETTINGS_DEFAULTS };
  const merged = { ...SITE_SETTINGS_DEFAULTS };
  for (const [k, v] of Object.entries(server)) {
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    merged[k] = v;
  }
  return merged;
}

/** Very small in-module cache so multiple components share one fetch. */
let _cached = null;
let _promise = null;

export function useSiteSettings() {
  const [settings, setSettings] = useState(_cached || SITE_SETTINGS_DEFAULTS);

  useEffect(() => {
    if (_cached) return;
    if (!_promise) {
      _promise = api
        .get("/public/settings")
        .then((r) => {
          _cached = mergeSettings(r.data);
          return _cached;
        })
        .catch(() => {
          _cached = { ...SITE_SETTINGS_DEFAULTS };
          return _cached;
        });
    }
    let alive = true;
    _promise.then((v) => {
      if (alive) setSettings(v);
    });
    return () => {
      alive = false;
    };
  }, []);

  return settings;
}

/**
 * Build the list of active social links from settings.
 * Hides any icon whose URL is empty, per the M3.5 spec.
 */
export function socialLinksFrom(settings) {
  const links = [
    { key: "instagram", label: "Instagram", url: settings.instagram_url },
    { key: "tiktok", label: "TikTok", url: settings.tiktok_url },
    { key: "facebook", label: "Facebook", url: settings.facebook_url },
    { key: "youtube", label: "YouTube", url: settings.youtube_url },
    { key: "whatsapp", label: "WhatsApp", url: settings.whatsapp_url },
  ];
  return links.filter((l) => (l.url || "").trim().length > 0);
}
